import { Schema } from "effect";

/**
 * Live-update channel for item on/off changes. Separate from the orders
 * channel so Landpad's order list never refetches for a menu change.
 */
export const ITEM_AVAILABILITY_CHANNEL = "/default/item-availability";

/** Square catalog and location IDs. */
const SquareId = Schema.String.pipe(Schema.pattern(/^[A-Za-z0-9_-]{1,192}$/));

/** Each market (Square location) has its own switches. */
export const ItemAvailabilityLocationPathSchema = Schema.Struct({
  locationId: SquareId,
});

export const ItemAvailabilityPathSchema = Schema.Struct({
  locationId: SquareId,
  itemId: SquareId,
});

/** `GET /catalog/:merchantId/items?locationId=`: the menu for one market. */
export const CatalogQuerySchema = Schema.Struct({
  locationId: Schema.optional(SquareId),
});

/**
 * Square's standard dietary preferences, set per item in Square
 * (`food_and_beverage_details.dietary_preferences`).
 */
export const DietaryTagSchema = Schema.Literal(
  "vegan",
  "vegetarian",
  "gluten-free",
  "dairy-free",
  "nut-free",
  "halal",
  "kosher",
);
export type DietaryTag = typeof DietaryTagSchema.Type;

/** The letters on a menu card's dietary badge, as Australian menus print them. */
export const DIETARY_BADGES: Readonly<Record<DietaryTag, string>> = {
  vegan: "VG",
  vegetarian: "V",
  "gluten-free": "GF",
  "dairy-free": "DF",
  "nut-free": "NF",
  halal: "H",
  kosher: "K",
};

/** `PUT /item-availability/:locationId/:itemId`: switch one Square item on or off at one market. */
export const PutItemAvailabilityBodySchema = Schema.Struct({
  available: Schema.Boolean,
});
export type PutItemAvailabilityBody = typeof PutItemAvailabilityBodySchema.Type;

/** One Square catalog item and whether staff are selling it. */
export const ItemAvailabilityEntrySchema = Schema.Struct({
  id: Schema.NonEmptyString,
  name: Schema.String,
  category: Schema.String,
  image: Schema.optional(Schema.String),
  dietary: Schema.optional(Schema.Array(DietaryTagSchema)),
  available: Schema.Boolean,
});
export type ItemAvailabilityEntry = typeof ItemAvailabilityEntrySchema.Type;

/**
 * Every item sold at one market with its on/off state there. Square stock
 * counts are ignored: an item is available until staff switch it off.
 */
export class OkItemAvailability extends Schema.TaggedClass<OkItemAvailability>()(
  "OkItemAvailability",
  {
    locationId: Schema.NonEmptyString,
    items: Schema.Array(ItemAvailabilityEntrySchema),
  },
) {}

/** The change is saved. `delayed` means open screens may not hear about it yet. */
export class OkItemAvailabilityChanged extends Schema.TaggedClass<OkItemAvailabilityChanged>()(
  "OkItemAvailabilityChanged",
  {
    locationId: Schema.NonEmptyString,
    itemId: Schema.NonEmptyString,
    available: Schema.Boolean,
    liveUpdate: Schema.Literal("sent", "delayed"),
  },
) {}

/** Storage or Square failed; safe to show staff. */
export class ErrItemAvailabilityUnavailable extends Schema.TaggedError<ErrItemAvailabilityUnavailable>()(
  "ErrItemAvailabilityUnavailable",
  { message: Schema.String },
) {}

/** Published on {@link ITEM_AVAILABILITY_CHANNEL} after every saved change. */
export const ItemAvailabilityEventSchema = Schema.Struct({
  type: Schema.Literal("item.availability"),
  payload: Schema.Struct({
    locationId: Schema.NonEmptyString,
    itemId: Schema.NonEmptyString,
    available: Schema.Boolean,
  }),
});
export type ItemAvailabilityEvent = typeof ItemAvailabilityEventSchema.Type;
